/**
 * Soll-Stunden Audit-Report (Admin).
 * 
 * Admin-Diagnose für die monatliche Lohn-Übergabe-Kontrolle.
 * Listet pro Mitarbeiter:in: Werktage, Soll/Ist/Überstunden, Abwesenheits-
 * Aufschlüsselung (Urlaub, Krankheit, Kind krank, Elternzeit, Krank ohne Schein).
 * Filterbar nach Standort/Niederlassung. Export als CSV/XLSX (DATEV-tauglich).
 */
import React, { useState, useEffect } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import {
  ClipboardCheck, Download, AlertTriangle, RefreshCw, FileSpreadsheet, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { workingHoursAuditAPI, locationsAPI, branchesAPI, companyAPI } from '../lib/api';
import { OrgUnitFilter, orgUnitToLegacyParams } from '../components/OrgUnitFilter';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function WorkingHoursAudit({ embedded = false }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [locationId, setLocationId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [orgUnit, setOrgUnit] = useState('all');
  const [orgTree, setOrgTree] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    Promise.all([locationsAPI.getAll(), branchesAPI.getAll(), companyAPI.getOrgTree().catch(() => ({ data: {} }))])
      .then(([locRes, brRes, treeRes]) => {
        setLocations(locRes.data || []);
        setBranches(brRes.data || []);
        setOrgTree(treeRes.data?.tree || []);
        setOrgLevels(treeRes.data?.levels || []);
      })
      .catch(() => {});
  }, []);

  const handleOrgUnitChange = (v) => {
    setOrgUnit(v);
    const p = orgUnitToLegacyParams(orgTree, v);
    setLocationId(p.location_id || '');
    setBranchId(p.branch_id || '');
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (locationId) params.location_id = locationId;
      if (branchId) params.branch_id = branchId;
      const { data } = await workingHoursAuditAPI.getAudit(params);
      setData(data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Audit konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, locationId, branchId]);

  const handleExport = (fmt) => {
    const params = { year, month, format: fmt };
    if (locationId) params.location_id = locationId;
    if (branchId) params.branch_id = branchId;
    window.open(workingHoursAuditAPI.exportUrl(params), '_blank');
  };

  const summary = data?.summary;
  const rows = data?.rows || [];
  const years = [today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="working-hours-audit-page">
        {/* Header + Filter */}
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <ClipboardCheck className="h-7 w-7 text-primary" />
              Soll-Stunden Audit
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Monatliche Übersicht: Werktage, Abwesenheiten, Soll/Ist/Überstunden — Lohn-Übergabe-tauglich.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="w-[140px]" data-testid="audit-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="w-[100px]" data-testid="audit-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="w-[240px]">
              <OrgUnitFilter
                tree={orgTree}
                levels={orgLevels}
                value={orgUnit}
                onChange={handleOrgUnitChange}
                showLabel={false}
                testId="audit-org-unit-select"
              />
            </div>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default" disabled={loading || rows.length === 0} data-testid="audit-export-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="audit-export-csv">
                  <FileText className="h-4 w-4 mr-2" />
                  CSV (für DATEV)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="audit-export-xlsx">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="audit-summary">
            <SummaryCard label="Mitarbeiter:innen" value={data.row_count} tone="neutral" />
            <SummaryCard label="Soll-Stunden ges." value={`${summary.total_target_hours} h`} tone="neutral" />
            <SummaryCard label="Ist-Stunden ges." value={`${summary.total_actual_hours} h`} tone="neutral" />
            <SummaryCard
              label="Überstunden ges."
              value={`${summary.total_overtime_hours > 0 ? '+' : ''}${summary.total_overtime_hours} h`}
              tone={summary.total_overtime_hours < 0 ? 'critical' : 'ok'}
            />
            <SummaryCard
              label="ArbZG-Warnungen"
              value={summary.non_compliant_count}
              tone={summary.non_compliant_count > 0 ? 'critical' : 'ok'}
            />
          </div>
        )}

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {MONTHS[month - 1]} {year}{locationId && ' • gefiltert'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Keine Mitarbeiter:innen für die gewählten Filter.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="audit-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Personalnr.</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Standort</TableHead>
                      <TableHead className="text-right">Werktage</TableHead>
                      <TableHead className="text-right">Soll</TableHead>
                      <TableHead className="text-right">Ist</TableHead>
                      <TableHead className="text-right">Überstd.</TableHead>
                      <TableHead className="text-center" title="Urlaub">U</TableHead>
                      <TableHead className="text-center" title="Krankheit">K</TableHead>
                      <TableHead className="text-center" title="Kind krank">KK</TableHead>
                      <TableHead className="text-center" title="Elternzeit">EZ</TableHead>
                      <TableHead className="text-center" title="Krank ohne Schein">KO</TableHead>
                      <TableHead className="text-right" title="Ø h pro Werktag, 6 Monate">Ø h/T</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const a = r.absence_days;
                      const ovValue = r.overtime_hours ?? 0;
                      return (
                        <TableRow key={r.employee_id} data-testid={`audit-row-${r.employee_id}`}>
                          <TableCell className="font-mono text-xs">{r.personnel_number || '—'}</TableCell>
                          <TableCell className="font-medium">{r.last_name}, {r.first_name}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">{r.location_name || '—'}</TableCell>
                          <TableCell className="text-right">{r.business_days}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.target_hours} h</TableCell>
                          <TableCell className="text-right tabular-nums">{r.actual_hours} h</TableCell>
                          <TableCell className={`text-right tabular-nums font-medium ${ovValue < 0 ? 'text-red-600 dark:text-red-400' : ovValue > 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                            {ovValue > 0 ? '+' : ''}{ovValue} h
                          </TableCell>
                          <AbsenceCell value={a.vacation} color="emerald" />
                          <AbsenceCell value={a.sick} color="red" />
                          <AbsenceCell value={a.child_sick} color="orange" />
                          <AbsenceCell value={a.parental} color="blue" />
                          <AbsenceCell value={a.sick_no_note} color="rose" />
                          <TableCell className="text-right text-xs">
                            {r.avg_daily_hours_6m ?? '—'}
                            {!r.avg_6m_compliant && (
                              <AlertTriangle className="inline h-3 w-3 ml-1 text-amber-500" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MaybeLayout>
  );
}

const SummaryCard = ({ label, value, tone }) => {
  const toneCls = {
    neutral: 'bg-muted/30',
    ok: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
    critical: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900',
  }[tone] || '';
  return (
    <Card className={toneCls}>
      <CardContent className="p-4">
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  );
};

const COLOR_CLASSES = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
};

const AbsenceCell = ({ value, color }) => (
  <TableCell className="text-center">
    {value > 0 ? (
      <Badge className={`${COLOR_CLASSES[color]} font-mono text-[10px] px-1.5 py-0`} variant="secondary">
        {value}
      </Badge>
    ) : (
      <span className="text-muted-foreground/40 text-xs">·</span>
    )}
  </TableCell>
);
